import { Project } from '@/types/projects'
import {
  Card,
  CardActionArea,
  Stack,
  Typography,
  Button,
  useTheme,
  Box,
  Tooltip,
  CardActionAreaProps,
  BoxProps,
} from '@mui/material'
import { Link, useNavigate } from 'react-router-dom'
import FileUploadProvider from '@/components/upload-files/FileUploadProvider'
import ProjectCardDropZone from '@/components/drop-zone/ProjectCardDropZone'
import TagChip from '@/components/tags/TagChip'
import { useGetWorkflows } from '@/service-library/hooks/workflows'
import { useGetProjectTemplates } from '@/service-library/hooks/project-templates'

export type ProjectCardProps = {
  project: Project
}

export default function ProjectCard({ project }: ProjectCardProps) {
  const theme = useTheme()
  const navigate = useNavigate()
  const { id: projectId, name, setup_state } = project

  const { workflows } = useGetWorkflows({
    filters: {
      limit: '1',
      project_id: projectId,
      code: 'basic',
    },
  })

  const { projectTemplates } = useGetProjectTemplates({
    filters: {
      limit: '1',
      project_id: projectId,
    },
  })

  const isSetupComplete = setup_state === 'complete'

  const Wrapper: React.FC<CardActionAreaProps & BoxProps> = isSetupComplete
    ? CardActionArea
    : Box

  const wrapperProps = isSetupComplete
    ? {
        onClick: () => {
          navigate(projectId)
        },
      }
    : {}

  const content = (
    <Card sx={{ height: '100%' }}>
      <Wrapper {...wrapperProps} sx={{ height: '100%' }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ p: 2 }}
          spacing={2}
        >
          <Stack spacing={0.5}>
            <Typography variant="subtitle1">{name}</Typography>
            {setup_state !== 'complete' && (
              <Typography
                variant="caption"
                color={theme.palette.text.secondary}
              >
                This project has not been fully configured. Complete the project
                setup to continue.
              </Typography>
            )}

            <Stack direction="row" spacing={1}>
              {project.has_pub_models && (
                <Tooltip title="This project manages one or more published models.">
                  <Box sx={{ display: 'flex' }}>
                    <TagChip
                      tag={{
                        color: 'teal',
                        name: 'Published Model',
                      }}
                    />
                  </Box>
                </Tooltip>
              )}
              {projectTemplates.length > 0 && (
                <Tooltip title="A project template was created from this project.">
                  <Box sx={{ display: 'flex' }}>
                    <TagChip
                      tag={{
                        color: 'blue',
                        name: 'Template Project',
                      }}
                    />
                  </Box>
                </Tooltip>
              )}
            </Stack>

            {setup_state !== 'complete' && (
              <Stack spacing={2}>
                <Box>
                  <Button
                    variant="text"
                    component={Link}
                    to={`/project-setup/${setup_state}?project_id=${projectId}`}
                  >
                    Complete Setup
                  </Button>
                </Box>
              </Stack>
            )}
          </Stack>
        </Stack>
      </Wrapper>
    </Card>
  )

  if (!workflows.length) {
    return content
  }

  return (
    <FileUploadProvider project={project} workflowId={workflows[0].id}>
      <ProjectCardDropZone project={project} enabled={!!workflows[0].id}>
        {content}
      </ProjectCardDropZone>
    </FileUploadProvider>
  )
}
